import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { AdminSidebar, SkeletonCard, EmptyState } from "@/components/AdminLayout";
import api from "@/utils/api";

export default function CourseManagement() {
  const [courses, setCourses] = useState([]);
  const [videos, setVideos] = useState([]);
  const [showBuilder, setShowBuilder] = useState(false);
  const [showVideoUpload, setShowVideoUpload] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [allWorkers, setAllWorkers] = useState([]);
  const [selectedWorkers, setSelectedWorkers] = useState([]);
  const [editingCourse, setEditingCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  
  // Course form state
  const [courseForm, setCourseForm] = useState({
    title: "",
    description: "",
    thumbnail_url: "",
    modules: [],
    final_quiz: [],
    expiry_days: 180,
    passing_score: 70,
    is_active: true
  });
  
  // Video upload state
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [coursesRes, videosRes, workersRes] = await Promise.all([
        api.get("/courses"),
        api.get("/videos"),
        api.get("/users")
      ]);
      setCourses(coursesRes.data);
      setVideos(videosRes.data);
      setAllWorkers(workersRes.data.filter(u => u.role === "worker"));
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const title = prompt("Enter video title:");
    if (!title) return;

    setUploadingVideo(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("title", title);

      const response = await api.post("/videos/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      toast.success("Video uploaded and transcoded successfully!");
      setShowVideoUpload(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Video upload failed");
    } finally {
      setUploadingVideo(false);
      setUploadProgress(0);
    }
  };

  const addModule = () => {
    const newModule = {
      title: "",
      description: "",
      video_url: "",
      video_duration: 300,
      order: courseForm.modules.length,
      quiz_questions: []
    };
    setCourseForm({
      ...courseForm,
      modules: [...courseForm.modules, newModule]
    });
  };

  const updateModule = (index, field, value) => {
    const updatedModules = [...courseForm.modules];
    updatedModules[index][field] = value;
    setCourseForm({ ...courseForm, modules: updatedModules });
  };

  const removeModule = (index) => {
    const updatedModules = courseForm.modules.filter((_, i) => i !== index);
    setCourseForm({ ...courseForm, modules: updatedModules });
  };

  const addQuizQuestion = (moduleIndex) => {
    const newQuestion = {
      question: "",
      options: ["", "", "", ""],
      correct_answer: 0
    };
    const updatedModules = [...courseForm.modules];
    updatedModules[moduleIndex].quiz_questions.push(newQuestion);
    setCourseForm({ ...courseForm, modules: updatedModules });
  };

  const updateQuizQuestion = (moduleIndex, questionIndex, field, value) => {
    const updatedModules = [...courseForm.modules];
    updatedModules[moduleIndex].quiz_questions[questionIndex][field] = value;
    setCourseForm({ ...courseForm, modules: updatedModules });
  };

  const saveCourse = async () => {
    if (!courseForm.title || courseForm.modules.length === 0) {
      toast.error("Please add title and at least one module");
      return;
    }

    try {
      if (editingCourse) {
        await api.put(`/courses/${editingCourse.id}`, courseForm);
        toast.success("Course updated successfully");
      } else {
        await api.post("/courses/builder", courseForm);
        toast.success("Course created successfully");
      }
      setShowBuilder(false);
      setEditingCourse(null);
      setCourseForm({
        title: "",
        description: "",
        thumbnail_url: "",
        modules: [],
        final_quiz: [],
        expiry_days: 180,
        passing_score: 70,
        is_active: true
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save course");
    }
  };

  const toggleCourseStatus = async (courseId, currentStatus) => {
    try {
      await api.put(`/courses/${courseId}`, { is_active: !currentStatus });
      toast.success(currentStatus ? "Course unpublished" : "Course published");
      fetchData();
    } catch (error) {
      toast.error("Failed to update course status");
    }
  };

  const openAssignModal = (course) => {
    setSelectedCourse(course);
    setSelectedWorkers([]);
    setShowAssignModal(true);
  };

  const handleAssignCourse = async () => {
    if (selectedWorkers.length === 0) {
      toast.error("Please select at least one worker");
      return;
    }

    try {
      // Assign course to each selected worker
      const assignments = selectedWorkers.map(workerId => 
        api.post(`/assignments?worker_id=${workerId}&course_id=${selectedCourse.id}`)
      );
      
      await Promise.all(assignments);
      
      toast.success(`Course assigned to ${selectedWorkers.length} worker(s)`);
      setShowAssignModal(false);
      setSelectedWorkers([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign course");
    }
  };

  const toggleWorkerSelection = (workerId) => {
    setSelectedWorkers(prev => 
      prev.includes(workerId)
        ? prev.filter(id => id !== workerId)
        : [...prev, workerId]
    );
  };

  const selectAllWorkers = () => {
    setSelectedWorkers(allWorkers.map(w => w.id));
  };

  const selectByDepartment = (department) => {
    const deptWorkers = allWorkers.filter(w => w.department === department);
    setSelectedWorkers(deptWorkers.map(w => w.id));
  };

  if (loading) {
    return (
      <div className="flex">
        <AdminSidebar active="courses" />
        <div className="admin-content">
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="h-10 bg-slate-200 rounded w-64 mb-2 animate-pulse"></div>
              <div className="h-5 bg-slate-200 rounded w-48 animate-pulse"></div>
            </div>
          </div>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => <SkeletonCard key={i} />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex">
      <AdminSidebar active="courses" />
      <div className="admin-content">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900" data-testid="courses-title">Course Management</h1>
            <p className="text-slate-600 mt-2">{courses.length} courses available</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Button
              onClick={() => setShowVideoUpload(true)}
              variant="outline"
              className="border-[#2e7fd4] text-[#2e7fd4] hover:bg-[#2e7fd4] hover:text-white"
              data-testid="upload-video-button"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Upload Video
            </Button>
            <Button
              onClick={() => {
                setShowBuilder(true);
                setEditingCourse(null);
              }}
              className="bg-[#0a2240] hover:bg-[#0f325c] text-white"
              data-testid="create-course-button"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create Course
            </Button>
          </div>
        </div>

        {/* Courses List */}
        {courses.length === 0 ? (
          <EmptyState
            title="No Courses Yet"
            description="Create your first training course to get started"
            action={
              <Button onClick={() => setShowBuilder(true)} className="bg-[#0a2240] hover:bg-[#0f325c] text-white">
                Create Course
              </Button>
            }
          />
        ) : (
          <div className="space-y-4">
          {courses.map((course) => (
            <div
              key={course.id}
              className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-bold text-slate-900">{course.title}</h3>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        course.is_active
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {course.is_active ? "Published" : "Unpublished"}
                    </span>
                  </div>
                  <p className="text-slate-600 mb-3">{course.description}</p>
                  <div className="flex gap-4 text-sm text-slate-500">
                    <span>{course.modules?.length || 0} modules</span>
                    <span>•</span>
                    <span>{course.passing_score}% passing score</span>
                    {course.expiry_days && (
                      <>
                        <span>•</span>
                        <span>Expires in {course.expiry_days} days</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      setEditingCourse(course);
                      setCourseForm(course);
                      setShowBuilder(true);
                    }}
                    variant="outline"
                    size="sm"
                  >
                    Edit
                  </Button>
                  <Button
                    onClick={() => openAssignModal(course)}
                    variant="outline"
                    size="sm"
                    className="border-[#0e9f8e] text-[#0e9f8e] hover:bg-[#0e9f8e] hover:text-white"
                  >
                    Assign Workers
                  </Button>
                  <Button
                    onClick={() => toggleCourseStatus(course.id, course.is_active)}
                    variant="outline"
                    size="sm"
                  >
                    {course.is_active ? "Unpublish" : "Publish"}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
        )}

        {/* Video Upload Modal */}
        {showVideoUpload && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Upload Training Video</h2>
              
              {uploadingVideo ? (
                <div>
                  <p className="text-slate-600 mb-4">Uploading and transcoding...</p>
                  <div className="w-full bg-slate-200 rounded-full h-4">
                    <div
                      className="bg-[#2e7fd4] h-4 rounded-full transition-all"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <p className="text-sm text-slate-500 mt-2 text-center">{uploadProgress}%</p>
                </div>
              ) : (
                <div>
                  <Label htmlFor="video-file">Select Video File (MP4, MOV, AVI)</Label>
                  <Input
                    id="video-file"
                    type="file"
                    accept="video/*"
                    onChange={handleVideoUpload}
                    className="mt-2"
                  />
                  <p className="text-sm text-slate-500 mt-2">
                    Video will be transcoded to HLS with multiple quality levels
                  </p>
                </div>
              )}

              <div className="flex gap-3 mt-6">
                <Button
                  onClick={() => setShowVideoUpload(false)}
                  variant="outline"
                  className="flex-1"
                  disabled={uploadingVideo}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Course Builder Modal */}
        {showBuilder && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white rounded-2xl p-8 max-w-4xl w-full mx-4 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                {editingCourse ? "Edit Course" : "Create New Course"}
              </h2>

              <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-4">
                {/* Basic Info */}
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="title">Course Title</Label>
                    <Input
                      id="title"
                      value={courseForm.title}
                      onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })}
                      placeholder="Safety Training Course"
                    />
                  </div>

                  <div>
                    <Label htmlFor="description">Description</Label>
                    <textarea
                      id="description"
                      value={courseForm.description}
                      onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })}
                      className="w-full h-20 px-3 py-2 border border-slate-200 rounded-md"
                      placeholder="Course description"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="passing_score">Passing Score (%)</Label>
                      <Input
                        id="passing_score"
                        type="number"
                        value={courseForm.passing_score}
                        onChange={(e) => setCourseForm({ ...courseForm, passing_score: parseInt(e.target.value) })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="expiry_days">Expiry (days)</Label>
                      <Input
                        id="expiry_days"
                        type="number"
                        value={courseForm.expiry_days}
                        onChange={(e) => setCourseForm({ ...courseForm, expiry_days: parseInt(e.target.value) })}
                      />
                    </div>
                  </div>
                </div>

                {/* Modules */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-slate-900">Modules</h3>
                    <Button onClick={addModule} size="sm" variant="outline">
                      Add Module
                    </Button>
                  </div>

                  {courseForm.modules.map((module, index) => (
                    <div key={index} className="border border-slate-200 rounded-lg p-4 mb-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-slate-900">Module {index + 1}</h4>
                        <Button
                          onClick={() => removeModule(index)}
                          size="sm"
                          variant="ghost"
                          className="text-red-600"
                        >
                          Remove
                        </Button>
                      </div>

                      <div className="space-y-3">
                        <Input
                          placeholder="Module Title"
                          value={module.title}
                          onChange={(e) => updateModule(index, "title", e.target.value)}
                        />
                        <Input
                          placeholder="Description"
                          value={module.description}
                          onChange={(e) => updateModule(index, "description", e.target.value)}
                        />
                        <div>
                          <Label>Select Video</Label>
                          <select
                            value={module.video_url}
                            onChange={(e) => updateModule(index, "video_url", e.target.value)}
                            className="w-full h-10 px-3 border border-slate-200 rounded-md"
                          >
                            <option value="">Select a video</option>
                            {videos.map((video) => (
                              <option key={video.id} value={video.master_url}>
                                {video.title}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <Button
                            onClick={() => addQuizQuestion(index)}
                            size="sm"
                            variant="outline"
                            className="mt-2"
                          >
                            Add Quiz Question
                          </Button>
                          
                          {module.quiz_questions.map((q, qIndex) => (
                            <div key={qIndex} className="mt-3 p-3 bg-slate-50 rounded">
                              <Input
                                placeholder="Question"
                                value={q.question}
                                onChange={(e) => updateQuizQuestion(index, qIndex, "question", e.target.value)}
                                className="mb-2"
                              />
                              {q.options.map((opt, oIndex) => (
                                <Input
                                  key={oIndex}
                                  placeholder={`Option ${oIndex + 1}`}
                                  value={opt}
                                  onChange={(e) => {
                                    const newOptions = [...q.options];
                                    newOptions[oIndex] = e.target.value;
                                    updateQuizQuestion(index, qIndex, "options", newOptions);
                                  }}
                                  className="mb-1"
                                />
                              ))}
                              <select
                                value={q.correct_answer}
                                onChange={(e) => updateQuizQuestion(index, qIndex, "correct_answer", parseInt(e.target.value))}
                                className="w-full h-8 px-2 border border-slate-200 rounded mt-2"
                              >
                                <option value={0}>Option 1 is correct</option>
                                <option value={1}>Option 2 is correct</option>
                                <option value={2}>Option 3 is correct</option>
                                <option value={3}>Option 4 is correct</option>
                              </select>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 mt-6 pt-6 border-t border-slate-200">
                <Button
                  onClick={() => {
                    setShowBuilder(false);
                    setEditingCourse(null);
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={saveCourse}
                  className="flex-1 bg-[#0a2240] hover:bg-[#0f325c] text-white"
                >
                  {editingCourse ? "Update Course" : "Create Course"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Assign Workers Modal */}
        {showAssignModal && selectedCourse && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-8 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">
                Assign Workers to: {selectedCourse.title}
              </h2>
              <p className="text-slate-600 mb-6">
                Select individual workers or entire departments to assign this course.
              </p>

              {/* Quick Actions */}
              <div className="flex gap-2 mb-4">
                <Button onClick={selectAllWorkers} size="sm" variant="outline">
                  Select All
                </Button>
                {Array.from(new Set(allWorkers.map(w => w.department))).map(dept => (
                  <Button
                    key={dept}
                    onClick={() => selectByDepartment(dept)}
                    size="sm"
                    variant="outline"
                  >
                    {dept}
                  </Button>
                ))}
              </div>

              {/* Workers List */}
              <div className="border border-slate-200 rounded-lg max-h-96 overflow-y-auto mb-6">
                <table className="w-full">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectedWorkers.length === allWorkers.length}
                          onChange={(e) => e.target.checked ? selectAllWorkers() : setSelectedWorkers([])}
                          className="w-4 h-4"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Employee ID</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Department</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {allWorkers.map(worker => (
                      <tr key={worker.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedWorkers.includes(worker.id)}
                            onChange={() => toggleWorkerSelection(worker.id)}
                            className="w-4 h-4"
                          />
                        </td>
                        <td className="px-4 py-3">{worker.full_name}</td>
                        <td className="px-4 py-3">{worker.employee_id}</td>
                        <td className="px-4 py-3">{worker.department}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-slate-600">
                  {selectedWorkers.length} worker(s) selected
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    setShowAssignModal(false);
                    setSelectedWorkers([]);
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAssignCourse}
                  disabled={selectedWorkers.length === 0}
                  className="flex-1 bg-[#0a2240] hover:bg-[#0f325c] text-white"
                >
                  Assign to {selectedWorkers.length} Worker(s)
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
